import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader, MapPin, X, ArrowUpRight } from 'lucide-react';
import { findBusinesses } from '../../../grader/api/graderApi';

/*
 * The hero's single call-to-action and lead magnet: an owner.com-style action
 * pill that searches Google Places (debounced) and routes the visitor into the
 * free visibility scan (/competition-report). Same contract as CompetitionSearchLite.
 */
function HeroSearchPill() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const timeoutRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    if (query.trim().length < 3) {
      setSuggestions([]);
      setShowDropdown(false);
      setLoading(false);
      return undefined;
    }

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setLoading(true);
    setShowDropdown(true);

    let cancelled = false;
    timeoutRef.current = setTimeout(async () => {
      try {
        const data = await findBusinesses(query, 8000);
        if (cancelled) return;
        setSuggestions(data || []);
      } catch (error) {
        if (cancelled) return;
        setSuggestions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [query]);

  const readHoneypot = () => {
    const el = wrapperRef.current?.querySelector('input[data-honeypot="grader"]');
    return el && typeof el.value === 'string' ? el.value : '';
  };

  const goToReport = (searchText, selectedBusiness) => {
    if (readHoneypot()) return;
    const trimmed = searchText.trim();
    if (trimmed.length < 3) return;
    navigate(`/competition-report?query=${encodeURIComponent(trimmed)}`, {
      state: selectedBusiness ? { selectedBusiness } : undefined
    });
  };

  const handleSelect = (business) => {
    const searchText = `${business.name}${business.city ? ` ${business.city}` : ''}`;
    setQuery(searchText);
    setShowDropdown(false);
    goToReport(searchText, business);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (suggestions.length > 0) {
      handleSelect(suggestions[0]);
    } else {
      goToReport(query);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && suggestions.length > 0) {
      event.preventDefault();
      handleSelect(suggestions[0]);
    }
  };

  return (
    <div className="hero-pill-wrap" ref={wrapperRef}>
      <form className="hero-pill" onSubmit={handleSubmit}>
        <div className="hero-pill-field">
          <Search className="hero-pill-icon" size={18} strokeWidth={2.2} aria-hidden="true" />
          <input
            type="text"
            className="hero-pill-input"
            placeholder="Find your business name"
            aria-label="Find your business name"
            role="combobox"
            aria-expanded={showDropdown}
            aria-controls="hero-pill-listbox"
            aria-autocomplete="list"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (query.trim().length >= 3) setShowDropdown(true);
            }}
          />
          {query && (
            <button
              type="button"
              className="hero-pill-clear"
              onClick={() => {
                setQuery('');
                setSuggestions([]);
                setShowDropdown(false);
              }}
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>
        <button type="submit" className="hero-pill-cta">
          <span>Get my AI report</span>
          <ArrowUpRight size={16} strokeWidth={2.6} aria-hidden="true" />
        </button>

        {/* Honeypot - invisible to humans, tripped by naive bots. */}
        <input
          type="text"
          name="website"
          data-honeypot="grader"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          defaultValue=""
          style={{ position: 'absolute', left: '-9999px', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }}
        />
      </form>

      {showDropdown && (
        <div className="hero-pill-dropdown" id="hero-pill-listbox" role="listbox">
          {loading ? (
            <div className="hero-pill-status" aria-live="polite">
              <Loader size={16} className="hero-pill-spinner" />
              <span>Finding businesses&hellip;</span>
            </div>
          ) : suggestions.length > 0 ? (
            suggestions.map((business) => (
              <button
                key={business.id || `${business.name}-${business.city}`}
                type="button"
                role="option"
                aria-selected="false"
                className="hero-pill-item"
                onClick={() => handleSelect(business)}
              >
                <span className="hero-pill-item-name">{business.name}</span>
                <span className="hero-pill-item-meta">
                  <MapPin size={12} />
                  <span>{business.city || 'Local area'}</span>
                  <span>★ {business.rating || 'N/A'}</span>
                </span>
              </button>
            ))
          ) : query.trim().length >= 3 ? (
            <div className="hero-pill-status" aria-live="polite">
              <span>No businesses found. Try another search.</span>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default HeroSearchPill;
